import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Box, Group, Stack, Table, Loader } from '@mantine/core'
import { AppBadge, AppButton, AppCard, AppCheckbox, AppInput, AppNumberInput, AppPasswordInput, AppSwitch, AppTabs, AppText, AppTextarea } from '../../app/common/ui'
import { DatePickerInput } from '@mantine/dates'
import { Page, StatCard, AmmoScoreBadge, ConfirmModal } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { format } from 'date-fns'
import { toast } from 'react-toastify'

const FLAG_STATUS: Record<number, string> = { 0: 'Open', 1: 'Resolved', 2: 'Dismissed' }

const RESET_PWD_INPUT_STYLES = {
  input: {
    background: '#21262D',
    border: '1.5px solid #484F58',
    color: '#E6EDF3'
  }
} as const

export default observer(function AdminUserDetailPage () {
  const { username } = useParams<{ username: string }>()
  const history = useHistory()
  const { adminStore, userStore } = useStore()
  const u = adminStore.selectedUser

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [bio, setBio] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuspended, setIsSuspended] = useState(false)
  const [suspendedReason, setSuspendedReason] = useState('')
  const [suspendedUntil, setSuspendedUntil] = useState<Date | null>(null)
  const [delta, setDelta] = useState<number | ''>(0)
  const [detail, setDetail] = useState('')
  const [isVisibleToUser, setIsVisibleToUser] = useState(false)
  const [adjustModalOpen, setAdjustModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [resetNewPassword, setResetNewPassword] = useState('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState('')
  const [resetPwdError, setResetPwdError] = useState('')
  const [resetPwdModalOpen, setResetPwdModalOpen] = useState(false)

  useEffect(() => {
    if (!userStore.user?.isAdmin) {
      history.replace('/not-found')
      return
    }
    if (username) adminStore.loadUser(username)
  }, [userStore.user?.isAdmin, username])

  useEffect(() => {
    if (u) {
      setDisplayName(u.displayName ?? '')
      setEmail(u.email ?? '')
      setBio(u.bio ?? '')
      setIsAdmin(u.isAdmin)
      setIsSuspended(u.isSuspended)
      setSuspendedReason(u.suspendedReason ?? '')
      setSuspendedUntil(u.suspendedUntil ? new Date(u.suspendedUntil) : null)
    }
  }, [u])

  const handleSaveProfile = async () => {
    if (!username) return
    try {
      await adminStore.editUser(username, {
        displayName: displayName || undefined,
        email: email || undefined,
        bio: bio || undefined,
        isAdmin,
        isSuspended,
        suspendedReason: isSuspended ? suspendedReason : undefined,
        suspendedUntil: isSuspended && suspendedUntil ? suspendedUntil.toISOString() : null
      })
      toast.success('Profile updated')
    } catch (err) {
      toast.error('Failed to update profile')
    }
  }

  const handleAdjustReputation = () => {
    if (!username || detail.trim().length < 10) {
      toast.error('Detail must be at least 10 characters')
      return
    }
    setAdjustModalOpen(true)
  }

  const deltaNum = typeof delta === 'number' ? delta : 0

  const confirmAdjust = async () => {
    if (!username || deltaNum === 0) return
    try {
      await adminStore.adjustReputation(username, deltaNum, detail.trim(), isVisibleToUser)
      toast.success('Reputation adjusted')
      setAdjustModalOpen(false)
      setDelta(0)
      setDetail('')
      setIsVisibleToUser(false)
    } catch (err) {
      toast.error('Failed to adjust reputation')
    }
  }

  const confirmDelete = async () => {
    if (!username) return
    try {
      await adminStore.deleteUser(username)
      setDeleteModalOpen(false)
      toast.success('Account deleted')
      history.push('/admin/users')
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to delete account'
      toast.error(message)
    }
  }

  const validateResetPasswordFields = (): boolean => {
    setResetPwdError('')
    if (!resetNewPassword.trim()) {
      setResetPwdError('New password is required.')
      return false
    }
    if (resetNewPassword.length < 6) {
      setResetPwdError('Password must be at least 6 characters.')
      return false
    }
    if (!resetConfirmPassword.trim()) {
      setResetPwdError('Confirm password is required.')
      return false
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetPwdError('Passwords do not match.')
      return false
    }
    return true
  }

  const handleOpenResetPwdModal = () => {
    if (!validateResetPasswordFields()) return
    setResetPwdModalOpen(true)
  }

  const confirmResetPassword = async () => {
    if (!username || !validateResetPasswordFields()) return
    try {
      await adminStore.resetPassword(username, {
        newPassword: resetNewPassword,
        confirmPassword: resetConfirmPassword
      })
      toast.success('Password reset successfully.')
      setResetNewPassword('')
      setResetConfirmPassword('')
      setResetPwdError('')
      setResetPwdModalOpen(false)
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } }
      const detail = ax.response?.data?.detail
      setResetPwdError(typeof detail === 'string' ? detail : 'Failed to reset password.')
    }
  }

  if (!userStore.user?.isAdmin) return null
  if (adminStore.loadingDetail || !username) {
    return (
      <Page title="User">
        <Loader />
      </Page>
    )
  }
  if (!u) {
    return (
      <Page title="User">
        <AppText color="dimmed">User not found.</AppText>
      </Page>
    )
  }

  const isSelf = userStore.user?.username?.toLowerCase() === u.userName?.toLowerCase()
  const afterScore = (u.ammoScore ?? 0) + deltaNum

  return (
    <Page
      title=""
      breadcrumbs={[
        { label: 'Admin', href: '/admin/users' },
        { label: 'User Management', href: '/admin/users' },
        { label: u.displayName || u.userName }
      ]}
    >
      <Stack spacing="lg">
        {(u.spamCandidateCount ?? 0) > 0 && (
          <Box
            sx={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-red-1)',
              border: '1px solid var(--mantine-color-red-3)'
            }}
          >
            <AppText size="sm" weight={600} color="red">
              ⚠ This user has content flagged as spam-candidate ({u.spamCandidateCount} items)
            </AppText>
          </Box>
        )}
        <Group position="apart" align="flex-start">
          <Box>
            <AppText size="lg" weight={600}>
              {u.displayName || u.userName}
            </AppText>
            {!isSelf && (
              <AppButton color="red" variant="outline" size="xs" mt="sm" onClick={() => setDeleteModalOpen(true)}>
                Delete account
              </AppButton>
            )}
            <AppText size="sm" color="dimmed">
              @{u.userName}
            </AppText>
            <AppText size="sm" color="dimmed">
              {u.email}
            </AppText>
            <Group spacing="xs" mt="sm">
              <AmmoScoreBadge tier={u.ammoTier} score={u.ammoScore} size="lg" />
              {u.isSuspended
                ? (
                <AppBadge color="red">Suspended</AppBadge>
                  )
                : (
                <AppBadge color="green">Active</AppBadge>
                  )}
              {u.isAdmin && <AppBadge color="violet">Admin</AppBadge>}
              {u.createdOn && (
                <AppText size="xs" color="dimmed">
                  Joined {format(new Date(u.createdOn), 'MMM d, yyyy')}
                </AppText>
              )}
            </Group>
          </Box>
        </Group>

        <Group spacing="md">
          <StatCard label="AmmoScore" value={u.ammoScore} />
          <StatCard label="Followers" value={u.followersCount} />
          <StatCard label="Posts" value={u.postCount} />
          <StatCard label="Parts" value={u.partCount} />
          <StatCard label="Builds" value={u.buildCount} />
          <StatCard label="Flags Upheld Against" value={u.flagsUpheldAgainstUser} />
        </Group>

        <AppCard withBorder padding="md">
          <AppText weight={600} mb="sm">
            Edit profile
          </AppText>
          <Stack spacing="sm">
            <AppInput label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            <AppInput label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <AppTextarea label="Bio" value={bio} onChange={(e) => setBio(e.target.value)} minRows={2} />
            <AppSwitch
              label="Admin"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.currentTarget.checked)}
              disabled={isSelf}
            />
            <AppSwitch
              label="Suspended"
              checked={isSuspended}
              onChange={(e) => setIsSuspended(e.currentTarget.checked)}
            />
            {isSuspended && (
              <>
                <AppInput
                  label="Suspension reason"
                  value={suspendedReason}
                  onChange={(e) => setSuspendedReason(e.target.value)}
                  placeholder="Reason for suspension"
                />
                <DatePickerInput
                  label="Suspended until"
                  value={suspendedUntil}
                  onChange={setSuspendedUntil}
                  clearable
                />
              </>
            )}
            <AppButton onClick={handleSaveProfile} loading={adminStore.submitting}>
              Save
            </AppButton>
          </Stack>
        </AppCard>

        <AppCard withBorder padding="md">
          <AppText weight={600} mb="sm">
            Reputation adjustment
          </AppText>
          <Stack spacing="sm">
            <AppNumberInput
              label="Delta"
              value={delta}
              onChange={setDelta}
              placeholder="e.g. 10 or -5"
            />
            <AppTextarea
              label="Detail (min 10 characters)"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="e.g. Goodwill credit — account recovery after data migration"
              minRows={2}
            />
            <AppCheckbox
              label="Show this adjustment to the user on their profile"
              checked={isVisibleToUser}
              onChange={(e) => setIsVisibleToUser(e.currentTarget.checked)}
            />
            <AppText size="sm" color="dimmed">
              Current: {u.ammoScore} → After: {afterScore}
            </AppText>
            <AppButton
              color="yellow"
              onClick={handleAdjustReputation}
              disabled={deltaNum === 0 || detail.trim().length < 10 || adminStore.submitting}
            >
              Adjust reputation
            </AppButton>
          </Stack>
        </AppCard>

        {userStore.user?.isAdmin && (
          <AppCard withBorder padding="md">
            <AppText
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                color: 'var(--gs-muted)'
              }}
              mb="md"
            >
              RESET USER PASSWORD
            </AppText>
            <Stack spacing="sm">
              <Group grow align="flex-start">
                <AppPasswordInput
                  label="New Password"
                  placeholder="New password"
                  value={resetNewPassword}
                  onChange={(e) => {
                    setResetNewPassword(e.currentTarget.value)
                    if (resetPwdError) setResetPwdError('')
                  }}
                  styles={RESET_PWD_INPUT_STYLES}
                />
                <AppPasswordInput
                  label="Confirm Password"
                  placeholder="Confirm password"
                  value={resetConfirmPassword}
                  onChange={(e) => {
                    setResetConfirmPassword(e.currentTarget.value)
                    if (resetPwdError) setResetPwdError('')
                  }}
                  styles={RESET_PWD_INPUT_STYLES}
                />
              </Group>
              {resetPwdError && (
                <AppText size="sm" color="red">
                  {resetPwdError}
                </AppText>
              )}
              <AppButton
                fullWidth
                onClick={handleOpenResetPwdModal}
                disabled={adminStore.submitting}
                sx={{
                  backgroundColor: '#F85149',
                  color: '#fff',
                  border: 'none',
                  '&:hover': { backgroundColor: '#da3633' }
                }}
              >
                Reset Password
              </AppButton>
            </Stack>
          </AppCard>
        )}

        <ConfirmModal
          opened={adjustModalOpen}
          title="Confirm reputation adjustment"
          message={`You are adjusting ${u.userName} AmmoScore by ${deltaNum}. This action is logged. Reason: ${detail}`}
          confirmLabel="Adjust"
          variant="warning"
          onConfirm={confirmAdjust}
          onClose={() => setAdjustModalOpen(false)}
        />

        <ConfirmModal
          opened={deleteModalOpen}
          title="Delete account"
          message={`Permanently delete the account @${u.userName}? This will remove their profile, content, and all related data. This cannot be undone.`}
          confirmLabel="Delete account"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteModalOpen(false)}
        />

        <ConfirmModal
          opened={resetPwdModalOpen}
          title="Reset password"
          message={`Are you sure you want to reset ${u.displayName || u.userName}'s password? They will need to be notified of their new password manually.`}
          confirmLabel="Reset password"
          variant="danger"
          onConfirm={confirmResetPassword}
          onClose={() => setResetPwdModalOpen(false)}
        />

        <AppTabs defaultValue="about">
          <AppTabs.List>
            <AppTabs.Tab value="about">About (signals)</AppTabs.Tab>
            <AppTabs.Tab value="flagsFiled">Flags filed</AppTabs.Tab>
            <AppTabs.Tab value="flagsAgainst">Flags against</AppTabs.Tab>
            <AppTabs.Tab value="modRoles">Moderator roles</AppTabs.Tab>
            <AppTabs.Tab value="adminLog">Admin log</AppTabs.Tab>
          </AppTabs.List>
          <AppTabs.Panel value="about" pt="md">
            {u.signalCounters && (
              <Group spacing="md">
                <StatCard label="Post votes received" value={u.signalCounters.postVotesReceived} />
                <StatCard label="Post votes lost" value={u.signalCounters.postVotesLost} />
                <StatCard label="Part votes received" value={u.signalCounters.partVotesReceived} />
                <StatCard label="Part votes lost" value={u.signalCounters.partVotesLost} />
                <StatCard label="Build votes received" value={u.signalCounters.buildVotesReceived} />
                <StatCard label="Build votes lost" value={u.signalCounters.buildVotesLost} />
                <StatCard label="Approved parts" value={u.signalCounters.approvedPartsCount} />
                <StatCard label="Approved models" value={u.signalCounters.approvedModelsCount} />
              </Group>
            )}
          </AppTabs.Panel>
          <AppTabs.Panel value="flagsFiled" pt="md">
            <Table striped>
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(u.recentFlags || []).map((f) => (
                  <tr key={f.id}>
                    <td>{f.targetType} / {f.targetId}</td>
                    <td>{f.reason}</td>
                    <td>{FLAG_STATUS[f.status] ?? f.status}</td>
                    <td>{format(new Date(f.createdAt), 'MMM d, yyyy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(!u.recentFlags || u.recentFlags.length === 0) && <AppText color="dimmed">No flags filed.</AppText>}
          </AppTabs.Panel>
          <AppTabs.Panel value="flagsAgainst" pt="md">
            <Table striped>
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Reason</th>
                  <th>Resolution</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(u.recentFlagsAgainst || []).map((f) => (
                  <tr key={f.id}>
                    <td>{f.targetType} / {f.targetId}</td>
                    <td>{f.reason}</td>
                    <td>{f.resolution ?? '—'}</td>
                    <td>{format(new Date(f.createdAt), 'MMM d, yyyy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(!u.recentFlagsAgainst || u.recentFlagsAgainst.length === 0) && <AppText color="dimmed">No flags against.</AppText>}
          </AppTabs.Panel>
          <AppTabs.Panel value="modRoles" pt="md">
            <Stack spacing="xs">
              {(u.moderatorRoles || []).map((r, i) => (
                <AppText key={i}>
                  <AppBadge size="sm" variant="light">{r.scope}</AppBadge> granted {format(new Date(r.grantedAt), 'MMM d, yyyy')}
                </AppText>
              ))}
              {(!u.moderatorRoles || u.moderatorRoles.length === 0) && <AppText color="dimmed">No moderator roles.</AppText>}
            </Stack>
          </AppTabs.Panel>
          <AppTabs.Panel value="adminLog" pt="md">
            <Table striped>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Detail</th>
                  <th>By</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(u.recentAdminActions || []).map((a) => (
                  <tr key={a.id}>
                    <td>{a.action}</td>
                    <td style={{ maxWidth: 400 }}>{a.detail}</td>
                    <td>{a.adminUsername}</td>
                    <td>{format(new Date(a.createdAt), 'MMM d, yyyy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(!u.recentAdminActions || u.recentAdminActions.length === 0) && <AppText color="dimmed">No admin actions.</AppText>}
          </AppTabs.Panel>
        </AppTabs>
      </Stack>
    </Page>
  )
})
